/**
 * Helper pour formater les états d'abonnement Stripe
 * Documentation: https://stripe.com/docs/billing/subscriptions/overview#subscription-statuses
 */

/**
 * États d'abonnement Stripe officiels
 */
export type StripeSubscriptionStatus =
  | "trialing"
  | "active"
  | "incomplete"
  | "incomplete_expired"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "paused";

interface SubscriptionStatusConfig {
  label: string;
  description: string;
  variant: "default" | "success" | "warning" | "destructive" | "secondary";
  color: string;
}

/**
 * Configuration des états d'abonnement Stripe
 * Basée sur la documentation officielle Stripe
 */
const SUBSCRIPTION_STATUS_CONFIG: Record<
  StripeSubscriptionStatus,
  SubscriptionStatusConfig
> = {
  trialing: {
    label: "En période d'essai",
    description:
      "L'abonnement est actuellement en période d'essai. L'abonnement passe automatiquement à l'état actif lorsqu'un client effectue son premier paiement.",
    variant: "secondary",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
  },
  active: {
    label: "Actif",
    description:
      "L'abonnement est en règle. Le client a accès complet aux fonctionnalités.",
    variant: "success",
    color:
      "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
  },
  incomplete: {
    label: "Incomplet",
    description:
      "Le client doit effectuer un paiement dans les 23 heures suivant la création de l'abonnement pour l'activer, ou une action est requise pour le paiement.",
    variant: "warning",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
  },
  incomplete_expired: {
    label: "Incomplet expiré",
    description:
      "Le paiement initial de l'abonnement a échoué et le client n'a pas effectué de paiement dans les 23 heures suivant la création.",
    variant: "destructive",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  },
  past_due: {
    label: "En retard",
    description:
      "Le paiement de la dernière facture a échoué ou n'a pas été tenté. L'abonnement continue de créer des factures.",
    variant: "warning",
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
  },
  canceled: {
    label: "Annulé",
    description:
      "L'abonnement a été annulé. Cet état est définitif et ne peut pas être mis à jour.",
    variant: "destructive",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  },
  unpaid: {
    label: "Impayé",
    description:
      "La dernière facture n'a pas été réglée, mais l'abonnement reste actif. Révoquez l'accès à votre produit.",
    variant: "destructive",
    color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  },
  paused: {
    label: "En pause",
    description:
      "L'abonnement a terminé sa période d'essai sans moyen de paiement par défaut. Aucune facture n'est plus créée.",
    variant: "secondary",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
  },
};

const DEFAULT_STATUS_CONFIG: SubscriptionStatusConfig = {
  label: "Inconnu",
  description: "État de l'abonnement inconnu.",
  variant: "secondary",
  color: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
};

/**
 * Vérifie si le statut est un statut Stripe valide
 */
function isStripeStatus(status: string): status is StripeSubscriptionStatus {
  return status in SUBSCRIPTION_STATUS_CONFIG;
}

/**
 * Formate le statut d'abonnement en label lisible
 * @param status - Le statut Stripe de l'abonnement
 * @returns Le label formaté en français
 */
export function formatSubscriptionStatus(
  status: string | null | undefined,
): string {
  if (!status) return DEFAULT_STATUS_CONFIG.label;
  if (isStripeStatus(status)) {
    return SUBSCRIPTION_STATUS_CONFIG[status].label;
  }
  return DEFAULT_STATUS_CONFIG.label;
}

/**
 * Récupère la configuration complète d'un statut d'abonnement
 * @param status - Le statut Stripe de l'abonnement
 * @returns La configuration complète (label, description, variant, color)
 */
export function getSubscriptionStatusConfig(
  status: string | null | undefined,
): SubscriptionStatusConfig {
  if (!status) return DEFAULT_STATUS_CONFIG;
  if (isStripeStatus(status)) {
    return SUBSCRIPTION_STATUS_CONFIG[status];
  }
  return DEFAULT_STATUS_CONFIG;
}

/**
 * Récupère la description d'un statut d'abonnement
 * @param status - Le statut Stripe de l'abonnement
 * @returns La description du statut
 */
export function getSubscriptionStatusDescription(
  status: string | null | undefined,
): string {
  return getSubscriptionStatusConfig(status).description;
}

/**
 * Récupère le variant pour les composants UI (Badge, etc.)
 * @param status - Le statut Stripe de l'abonnement
 * @returns Le variant à utiliser
 */
export function getSubscriptionStatusVariant(
  status: string | null | undefined,
): SubscriptionStatusConfig["variant"] {
  return getSubscriptionStatusConfig(status).variant;
}

/**
 * Récupère les classes CSS pour le statut
 * @param status - Le statut Stripe de l'abonnement
 * @returns Les classes CSS Tailwind
 */
export function getSubscriptionStatusColor(
  status: string | null | undefined,
): string {
  return getSubscriptionStatusConfig(status).color;
}

/**
 * Vérifie si l'abonnement donne accès aux fonctionnalités
 * @param status - Le statut Stripe de l'abonnement
 * @returns true si l'utilisateur a accès
 */
export function hasSubscriptionAccess(
  status: string | null | undefined,
): boolean {
  if (!status) return false;
  // États qui donnent accès aux fonctionnalités
  const accessibleStatuses: StripeSubscriptionStatus[] = [
    "trialing",
    "active",
    "past_due",
  ];
  return accessibleStatuses.includes(status as StripeSubscriptionStatus);
}

/**
 * Vérifie si l'abonnement est dans un état critique nécessitant une action
 * @param status - Le statut Stripe de l'abonnement
 * @returns true si une action est requise
 */
export function requiresSubscriptionAction(
  status: string | null | undefined,
): boolean {
  if (!status) return false;
  const actionRequiredStatuses: StripeSubscriptionStatus[] = [
    "incomplete",
    "past_due",
    "unpaid",
    "paused",
    "incomplete_expired",
  ];
  return actionRequiredStatuses.includes(status as StripeSubscriptionStatus);
}

/**
 * Vérifie si l'abonnement est annulé
 * @param status - Le statut Stripe de l'abonnement
 * @returns true si l'abonnement est annulé
 */
export function isSubscriptionCanceled(
  status: string | null | undefined,
): boolean {
  return status === "canceled";
}

/**
 * Interface pour les données utilisateur nécessaires à la vérification d'accès
 */
interface SubscriptionUserData {
  subscriptionStatus?: string | null;
  subscriptionCancelledAt?: string | null;
  subscriptionEndDate?: string | null;
}

/**
 * Vérifie si l'utilisateur peut accéder au dashboard (a un abonnement valide)
 *
 * Politique d'accès :
 * - ACTIVE : Accès garanti jusqu'à la fin de la période payée (même si annulation programmée)
 * - TRIALING : Accès uniquement si NON annulé (coupé immédiatement si annulé)
 * - CANCELED : Accès jusqu'à endDate (période de grâce pour abonnements payés)
 * - Autres : Pas d'accès
 *
 * @param user - Les données d'abonnement de l'utilisateur
 * @returns true si l'utilisateur peut accéder au dashboard
 */
export function canAccessDashboard(user: SubscriptionUserData | null): boolean {
  if (!user) return false;

  const status = user.subscriptionStatus;
  if (!status) return false;

  // Active = toujours accès
  if (status === "active") {
    return true;
  }

  // Trial = accès seulement si PAS annulé
  if (status === "trialing") {
    return !user.subscriptionCancelledAt;
  }

  // Canceled = accès jusqu'à endDate (période de grâce)
  if (status === "canceled") {
    if (user.subscriptionEndDate) {
      return new Date() <= new Date(user.subscriptionEndDate);
    }
    return false;
  }

  // Autres statuts = pas d'accès
  return false;
}
